import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, signal } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { AskBobStandaloneData } from '../../../../models/workspace-overview.model';
import { WorkspaceOverviewStateService } from '../../../../services/workspace-overview-state.service';
import { WorkspaceOverviewService } from '../../../../services/workspace-overview.service';

/**
 * Ask Bob Standalone Widget
 *
 * Conversations, documents analyzed, output type splits (PDF/DOCX/XLSX), and
 * Ask vs Agentic mode bar. Reads state from `WorkspaceOverviewStateService`.
 *
 * Template binds:
 *  - `data()?.outputs.pdf` / `.docx` / `.xlsx` / `.others` — raw counts
 *  - `outputsSplit()` — percentage widths for the breakdown bar
 *  - `data()?.modeSplit.ask` / `.agentic` — mode percentages
 *
 * Tooltip: "Displays Bob Standalone activity — conversations held, files
 * generated, documents analyzed, and mode usage patterns".
 */
@Component({
    selector: 'app-ask-bob-standalone-widget',
    standalone: true,
    imports: [CommonModule, TooltipModule, SkeletonModule],
    templateUrl: './ask-bob-standalone-widget.component.html',
    styleUrl: './ask-bob-standalone-widget.component.scss',
})
export class AskBobStandaloneWidgetComponent implements OnInit {
    readonly data = signal<AskBobStandaloneData | null>(null);
    readonly loading = signal(false);

    /**
     * Percentage widths for the PDF / DOCX / XLSX / Others breakdown bar.
     * Sums to 100 (or is all-zero when there are no outputs).
     */
    readonly outputsSplit = computed(() => {
        const o = this.data()?.outputs;
        const total = o?.total || 1; // guard against divide-by-zero
        return {
            pdf: Math.round(((o?.pdf ?? 0) / total) * 100),
            docx: Math.round(((o?.docx ?? 0) / total) * 100),
            xlsx: Math.round(((o?.xlsx ?? 0) / total) * 100),
            others: Math.round(((o?.others ?? 0) / total) * 100),
        };
    });

    constructor(
        private readonly service: WorkspaceOverviewService,
        private readonly state: WorkspaceOverviewStateService,
    ) {
        effect(() => {
            this.fetch(this.state.viewMode(), this.state.period(), this.state.effectiveRange());
        });
    }

    ngOnInit(): void { /* initial fetch triggered by the effect above */ }

    private fetch(
        mode: ReturnType<WorkspaceOverviewStateService['viewMode']>,
        period: ReturnType<WorkspaceOverviewStateService['period']>,
        range: ReturnType<WorkspaceOverviewStateService['effectiveRange']>,
    ): void {
        this.loading.set(true);
        this.service.getAskBobStandalone({ mode, period, range }).subscribe({
            next: data => { this.data.set(data); this.loading.set(false); },
            error: () => this.loading.set(false),
        });
    }
}
